import React, { useEffect } from "react";
import {
  Box,
  Flex,
  Table as ChakraTable,
  TableBodyProps,
  TableCaption,
  TableContainer,
  TableHeadProps,
  TableProps as ChakraTableProps,
  Text
} from "@chakra-ui/react";
import { getNearestEarlyTimeElement } from "duck";
import Pagination from "../pagination";
import { TBody, THead } from "./components";
import { types } from "./duck";

interface TableProps extends Omit<ChakraTableProps, "children"> {
  data: any;
  theadProps?: TableHeadProps;
  theadHidden?: boolean;
  topCaption?: string;
  leftCaption?: string;
  tbodyProps?: TableBodyProps;
  loading?: boolean;
  rowSelection?: {
    selectedKeys: (string | number)[];
    onChange(keys: (string | number)[]): void;
  };
  children: types.Columns;
  editable?: boolean;
  totalCount?: number;
  perPage?: number;
  noDataMessage?: string;
  scrollToCurrentTime?: boolean;
}

const Table: React.FC<TableProps> = ({
  data = [],
  children,
  theadProps,
  theadHidden,
  topCaption,
  leftCaption,
  tbodyProps,
  loading,
  rowSelection,
  editable = false,
  totalCount,
  perPage,
  noDataMessage,
  scrollToCurrentTime = false,
  ...rest
}) => {
  const columns = (React.Children.toArray(children) as types.Columns).map(
    child => child.props
  );

  const tableContainerRef = React.useRef<HTMLDivElement>(null);

  const [refAcquired, setRefAcquired] = React.useState(false);
  const [visibleWidth, setVisibleWidth] = React.useState(0);

  React.useEffect(() => {
    setRefAcquired(true);
  }, []);

  React.useEffect(() => {
    setVisibleWidth(tableContainerRef?.current?.offsetWidth || 0);
  }, [refAcquired]);

  const handleSelect = (key: number | string) => {
    if (rowSelection) {
      const selected = rowSelection.selectedKeys.includes(key)
        ? rowSelection.selectedKeys.filter(val => val !== key)
        : [...rowSelection.selectedKeys, key];

      rowSelection.onChange(selected);
    }
  };

  const handleSelectAll = (selectAll: boolean) => {
    if (rowSelection && selectAll) {
      rowSelection.onChange(
        data.map((record: any) => record._id || record.fakeId)
      );
    } else if (rowSelection && !selectAll) {
      rowSelection.onChange([]);
    }
  };

  const hasVisiblePagination =
    typeof totalCount === "number" && totalCount > (perPage || 20);

  useEffect(() => {
    if (tableContainerRef.current && scrollToCurrentTime) {
      const updateScrollPosition = (
        tableColumns: types.ColumnProps[],
        container: HTMLDivElement
      ) => {
        const nearestElement = getNearestEarlyTimeElement(
          tableColumns.map(el => el.label) as string[]
        );
        const indexOfElement = columns.findIndex(
          el => el.label === nearestElement
        );
        const columnWidth = 106;
        const scrollLeft = columnWidth * (indexOfElement - 1);
        container.scrollTo({
          left: scrollLeft,
          behavior: "smooth"
        });
      };

      updateScrollPosition(columns, tableContainerRef.current);

      const intervalId = setInterval(() => {
        updateScrollPosition(
          columns,
          tableContainerRef.current as HTMLDivElement
        );
      }, 5 * 60 * 1000);

      return () => clearInterval(intervalId);
    }
  }, [refAcquired, scrollToCurrentTime]);

  return (
    <>
      <TableContainer
        ref={tableContainerRef}
        borderTopLeftRadius="lg"
        borderTopRightRadius="lg"
        borderBottomLeftRadius={hasVisiblePagination ? "none" : "lg"}
        borderBottomRightRadius={hasVisiblePagination ? "none" : "lg"}
        overflowX="auto"
        css={{
          "&::-webkit-scrollbar": {
            height: "4px",
            backgroundColor: "#313143"
          },
          "&::-webkit-scrollbar-track": {
            height: "4px",
            backgroundColor: "#EDF2F7"
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#313143",
            borderRadius: "24px"
          }
        }}
        bg="white"
        display="flex"
      >
        {leftCaption && (
          <Flex
            justify="center"
            align="center"
            w="37px"
            minH="100%"
            bg="gray.200"
            border="1px solid"
            borderColor="gray.100"
          >
            <Text
              color="gray.700"
              fontWeight="medium"
              fontSize="sm"
              transform="rotate(-90deg)"
            >
              {leftCaption}
            </Text>
          </Flex>
        )}
        <ChakraTable fontSize="sm" {...rest}>
          {topCaption && (
            <TableCaption
              border="1px solid"
              borderLeft={leftCaption ? "0" : "1"}
              borderColor="gray.100"
              mt={0}
              placement="top"
              textAlign="left"
              bg="gray.200"
              color="black"
            >
              {/* 86px - left caption width with borders & top caption right and left paddings and right border */}
              {/* 50px - top caption right and left paddings and borders */}
              <Box
                w={`${leftCaption ? visibleWidth - 86 : visibleWidth - 50}px`}
                textAlign="center"
              >
                {topCaption}
              </Box>
            </TableCaption>
          )}
          <THead
            {...theadProps}
            theadHidden={theadHidden}
            columns={columns}
            onSelectAll={
              rowSelection && !!data.length ? handleSelectAll : undefined
            }
            isIndeterminate={(rowSelection?.selectedKeys?.length || 0) > 0}
            isAllSelected={
              rowSelection?.selectedKeys?.length === data.length &&
              data.length > 0
            }
          />
          <TBody
            {...tbodyProps}
            data={data}
            loading={loading}
            noDataMessage={noDataMessage}
            isTableEditable={editable}
            columns={columns}
            onRowSelect={rowSelection && handleSelect}
            selectedRows={rowSelection?.selectedKeys || []}
          />
        </ChakraTable>
      </TableContainer>
      {totalCount !== undefined && (
        <Pagination
          hideOnSinglePage
          perPage={perPage}
          total={totalCount}
          bg="white"
          borderBottomRightRadius="lg"
          borderBottomLeftRadius="lg"
        />
      )}
    </>
  );
};

export default Table;
